import { isTMessage, TMessage } from "@/i18n/tt";
import MessageStore from "@/stores/AlertMessageStore";
import { DCCallback, useTranslation } from "@/stores/RootStoreContext";
import { Markdown } from "@/text/Markdown";
import { classNames } from "@/utils/classNames";
import { isReactElement } from "@/utils/reactHelpers";
import { Popover as BasePopover } from "@base-ui/react/popover";
import { isFunction } from "lodash";
import { observer } from "mobx-react";
import { forwardRef, ReactElement } from "react";
import styles from "./Alert.module.css";
import { Box } from "./Box";
import { BoxProps } from "./boxHelpers";
import { IconButton } from "./Button";
import { IconProps } from "./Icon";
import { renderIcon } from "./iconHelpers";
import { IconName } from "./iconMapping";

export type AlertProps = BoxProps & {
  message: ReactElement | string | number | TMessage | (() => ReactElement);
  type: "info" | "warning" | "error" | "success";
  icon?: boolean | ReactElement | IconProps["name"] | IconProps;
  onClose?: DCCallback;
  centered?: boolean;
};

const iconMapping: Record<AlertProps["type"], IconName> = {
  info: "info",
  warning: "warning",
  success: "check-circle",
  error: "warning",
};

export const Alert = forwardRef<HTMLDivElement, AlertProps>(function Alert(
  { message, type, className, icon, onClose, centered, ...props },
  ref,
) {
  const { t } = useTranslation();
  return (
    <Box
      ref={ref}
      alignItems={"center"}
      rounded
      className={classNames(className, styles.alert, `bg-${type} color-${type}`)}
      paddingLeft={centered ? undefined : "md"}
      gap={false}
      {...props}
    >
      {icon && <Box paddingY={"sm"}>{renderIcon(icon === true ? iconMapping[type] : icon)}</Box>}
      <Box display={"block"} grow padding={"sm"} textAlign={centered ? "center" : undefined}>
        {isReactElement(message) ? (
          message
        ) : isTMessage(message) ? (
          <Markdown value={t(message)} inheritLinkColor enableLinks />
        ) : isFunction(message) ? (
          message()
        ) : (
          message
        )}
      </Box>
      {onClose && (
        <div className={"pr-1"}>
          <IconButton color={"inherit"} icon={"x"} tooltip={t("close")} onClick={onClose} />
        </div>
      )}
    </Box>
  );
});

export const AlertContainer = observer(function AlertContainer({
  messageStore,
  offset,
}: {
  messageStore: MessageStore;
  offset?: "above" | "below";
}) {
  const message = messageStore.message;

  if (message) {
    return (
      <BasePopover.Root
        open={messageStore.messageVisible}
        onOpenChange={(open, event) => {
          if (!open) {
            switch (event.reason) {
              case "outside-press":
              case "escape-key":
                messageStore.hideMessage();
                break;
            }
          }
        }}
      >
        <BasePopover.Trigger />
        <BasePopover.Portal>
          <BasePopover.Positioner
            side="bottom"
            sideOffset={
              offset
                ? (params) => ((offset === "above" ? -1 : 1) * params.positioner.height) / 2
                : 0
            }
          >
            <BasePopover.Popup
              className={classNames(
                "data-starting-style:opacity-0",
                "data-starting-style:-translate-y-full",
                "data-ending-style:opacity-0",
                "transition",
                "duration-200",
              )}
            >
              <div className={"flex justify-center mx-4"}>
                <Alert
                  message={message}
                  type={messageStore.type}
                  dropShadow={"lg"}
                  maxWidth={"prose"}
                  grow
                  onClose={messageStore.hideMessage}
                />
              </div>
            </BasePopover.Popup>
          </BasePopover.Positioner>
        </BasePopover.Portal>
      </BasePopover.Root>
    );
  }
  return null;
});
